/**
 * questConfig.js — Quest definitions.
 *
 * Each quest evaluates against the live app state (s) and projection (proj).
 * Pure data + pure functions — zero React.
 *
 * Quest shape:
 *   id:        unique string key
 *   title:     short display name
 *   desc:      1-line description of what to do
 *   category:  "safety" | "invest" | "budget" | "streak" | "credit"
 *   icon:      emoji
 *   order:     sort priority (lower = earlier)
 *   check:     (s, proj) => boolean — is the quest complete right now?
 *   progress:  (s, proj) => 0..1 — how far along
 *   reward:    string — motivational message on completion
 *   requires:  quest id that must be completed first (optional)
 */

const DEF_CATS = []; // fallback if cats missing

function budgetTotal(s) {
  return (s.cats || DEF_CATS).reduce((a, x) => a + x.a, 0);
}

function budgetMet(s) {
  const cats = s.cats || DEF_CATS;
  return cats.length > 0 && cats.every((cat) => cat.a <= cat.b);
}

export const QUESTS = [
  /* ═══════ SAFETY ═══════ */
  {
    id: "emergency-1k",
    title: "First $1K Safety Net",
    desc: "Keep at least $1,000 in your chequing account.",
    category: "safety",
    icon: "🛡️",
    order: 1,
    check: (s) => s.cheq >= 1000,
    progress: (s) => Math.min(1, s.cheq / 1000),
    reward: "You just bought yourself breathing room.",
  },
  {
    id: "emergency-full",
    title: "Emergency Fund Complete",
    desc: "Fill your emergency fund to your target months of expenses.",
    category: "safety",
    icon: "🏰",
    order: 2,
    requires: "emergency-1k",
    check: (s) => {
      const target = budgetTotal(s) * (s.efTarget || 6);
      return target > 0 && s.cheq >= target;
    },
    progress: (s) => {
      const target = budgetTotal(s) * (s.efTarget || 6);
      return target > 0 ? Math.min(1, s.cheq / target) : 0;
    },
    reward: "Your safety net is locked in. Sleep easy.",
  },

  /* ═══════ CREDIT ═══════ */
  {
    id: "credit-zero",
    title: "Zero Balance",
    desc: "Pay off your credit card completely.",
    category: "credit",
    icon: "💳",
    order: 3,
    check: (s) => s.ccBal <= 0,
    progress: (s) => s.ccLim > 0 ? Math.min(1, 1 - (s.ccBal / s.ccLim)) : 1,
    reward: "No debt, no drag. Clean slate.",
  },
  {
    id: "credit-750",
    title: "Credit Score 750+",
    desc: "Build your credit score to the Excellent tier.",
    category: "credit",
    icon: "⭐",
    order: 7,
    check: (s) => s.cScore >= 750,
    progress: (s) => {
      const base = 600; // reasonable floor
      return Math.min(1, Math.max(0, (s.cScore - base) / (750 - base)));
    },
    reward: "Excellent credit. Doors are open.",
  },

  /* ═══════ BUDGET ═══════ */
  {
    id: "budget-boss",
    title: "Budget Boss",
    desc: "Stay at or under budget in every spending category.",
    category: "budget",
    icon: "🎯",
    order: 4,
    check: (s) => budgetMet(s),
    progress: (s) => {
      const cats = s.cats || DEF_CATS;
      if (cats.length === 0) return 0;
      const onBudget = cats.filter((cat) => cat.a <= cat.b).length;
      return onBudget / cats.length;
    },
    reward: "Every dollar accounted for. That's discipline.",
  },
  {
    id: "budget-sync-on",
    title: "Connect the Dots",
    desc: "Activate Budget → Projection sync to link your spending to your future.",
    category: "budget",
    icon: "🔗",
    order: 5,
    check: (s) => s.budgetSync === true,
    progress: (s) => s.budgetSync ? 1 : 0,
    reward: "Your budget now shapes your future. Every dollar counts.",
  },

  /* ═══════ INVEST ═══════ */
  {
    id: "nw-10k",
    title: "Five-Figure Club",
    desc: "Reach a net worth of $10,000.",
    category: "invest",
    icon: "📈",
    order: 6,
    check: (s) => s.nw0 >= 10000,
    progress: (s) => Math.min(1, s.nw0 / 10000),
    reward: "Five figures. This is where it starts to compound.",
  },
  {
    id: "nw-50k",
    title: "The $50K Milestone",
    desc: "Grow your net worth to $50,000.",
    category: "invest",
    icon: "🚀",
    order: 8,
    requires: "nw-10k",
    check: (s) => s.nw0 >= 50000,
    progress: (s) => Math.min(1, s.nw0 / 50000),
    reward: "Fifty thousand. Momentum is real now.",
  },
  {
    id: "nw-100k",
    title: "The Hardest $100K",
    desc: "Reach $100,000 net worth — the hardest milestone in wealth building.",
    category: "invest",
    icon: "💎",
    order: 10,
    requires: "nw-50k",
    check: (s) => s.nw0 >= 100000,
    progress: (s) => Math.min(1, s.nw0 / 100000),
    reward: "The first $100K is the hardest. Everything after this accelerates.",
  },

  /* ═══════ STREAKS ═══════ */
  {
    id: "streak-7",
    title: "One-Week Warrior",
    desc: "Maintain a 7-day check-in streak.",
    category: "streak",
    icon: "🔥",
    order: 9,
    check: (s) => (s.streak?.best || 0) >= 7,
    progress: (s) => Math.min(1, (s.streak?.best || 0) / 7),
    reward: "Strong week. Your wealth streak survives.",
  },
  {
    id: "streak-30",
    title: "30-Day Legend",
    desc: "Maintain a 30-day check-in streak. Consistency compounds.",
    category: "streak",
    icon: "⚡",
    order: 11,
    requires: "streak-7",
    check: (s) => (s.streak?.best || 0) >= 30,
    progress: (s) => Math.min(1, (s.streak?.best || 0) / 30),
    reward: "30 days. Most people never get here. You're different.",
  },
];
